import { useMemo } from 'react';
import {
  Trophy, MapPin, CalendarDays, Radio, Building2, TrendingUp, TrendingDown,
  Minus, Users, Sparkles, BadgeCheck,
} from 'lucide-react';
import { usePost } from '@/lib/useData';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { ErrorState } from '@/components/common/ErrorState';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { HorizontalBars } from '@/components/charts/HorizontalBars';
import { RecruiterEvolution } from '@/components/recruiters/RecruiterEvolution';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

const Kpi = ({ label, value, hint }) => (
  <div className="rounded-xl border bg-card p-3" data-testid="recruiter-kpi">
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="mt-0.5 font-display text-xl font-semibold tabular text-foreground">{value}</p>
    {hint && <p className="mt-0.5 text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

// Small "vs moyenne" delta indicator (higher-is-better unless invert).
const VsAvg = ({ value, avg, invert = false, pct = false }) => {
  if (avg === undefined || avg === null || !avg) return null;
  const diff = value - avg;
  const good = invert ? diff < 0 : diff > 0;
  const flat = Math.abs(diff) < (pct ? 0.005 : avg * 0.02);
  const Icon = flat ? Minus : good ? TrendingUp : TrendingDown;
  const cls = flat ? 'text-muted-foreground' : good ? 'text-emerald-600' : 'text-rose-500';
  const shown = pct ? `${diff >= 0 ? '+' : ''}${fmtNum(diff * 100, 1)} pts` : `${diff >= 0 ? '+' : ''}${fmtNum(diff, 2)}`;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] ${cls}`}>
      <Icon className="h-3 w-3" />{shown} vs moy.
    </span>
  );
};

const KpiVs = ({ label, value, delta }) => (
  <div className="rounded-xl border bg-card p-3">
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="mt-0.5 font-display text-xl font-semibold tabular text-foreground">{value}</p>
    <div className="mt-0.5">{delta}</div>
  </div>
);

const Callout = ({ icon: Icon, title, primary, secondary }) => (
  <div className="flex items-start gap-3 rounded-xl border bg-gradient-to-br from-[color:var(--tenant-accent-100)]/60 to-transparent p-4" data-testid="recruiter-callout">
    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl accent-bg-100">
      <Icon className="h-4 w-4 accent-text" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="font-display text-base font-semibold text-foreground break-words">{primary}</p>
      {secondary && <p className="text-xs text-muted-foreground break-words">{secondary}</p>}
    </div>
  </div>
);

const DistBar = ({ label, value, total, color = 'var(--tenant-accent-500)' }) => {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular font-medium">{fmtInt(value)} · {fmtNum(pct, 0)}%</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

export const RecruiterProfile = ({ recruiter, body }) => {
  const reqBody = useMemo(
    () => (recruiter ? { ...body, recruiter } : null),
    [recruiter, body]
  );
  const { data, isLoading, isError, refetch } = usePost('/admin/recruiters/profile', reqBody, !!recruiter);

  if (isError) {
    return <ErrorState body="Impossible de charger le profil de ce recruteur." onRetry={refetch} />;
  }

  if (isLoading || !data) {
    return <div className="grid grid-cols-1 gap-4 md:grid-cols-2"><CardSkeleton /><CardSkeleton /></div>;
  }

  const s = data.summary || {};
  const g = s.global || {};
  const bestDay = data.best_day;
  const bestSpot = data.best_spot;
  const genre = data.genre || {};
  const genreTotal = (genre.F || 0) + (genre.H || 0) + (genre.Autre || 0);

  const weekdayData = (data.by_weekday || []).map((w) => ({
    key: w.jour,
    value: w.bs_reel,
    color: bestDay && w.jour === bestDay.jour ? 'var(--tenant-accent-500)' : 'var(--tenant-accent-100)',
  }));
  const weekdaySub = {};
  (data.by_weekday || []).forEach((w) => {
    weekdaySub[w.jour] = [`${fmtNum(w.bs_par_jour, 2)} BS/j · ${w.jours_actifs} j · ${fmtPct(w.taux_transfo)}`];
  });

  const topSpots = (data.spots || []).slice(0, 8);
  const spotData = topSpots.map((sp) => ({
    key: sp.lieu,
    value: sp.bs_reel,
    color: bestSpot && sp.lieu === bestSpot.lieu ? 'var(--tenant-accent-500)' : 'var(--tenant-accent-100)',
  }));
  const spotSub = {};
  topSpots.forEach((sp) => { spotSub[sp.lieu] = [`${fmtNum(sp.bs_par_jour, 2)} BS/j · ${sp.jours_actifs} j · ${fmtPct(sp.taux_transfo)}`]; });

  const ageData = (data.age_distribution || []).map((a) => ({ key: a.label, value: a.value }));
  const byAssoc = data.by_association || [];
  const bestAssoc = data.best_association;

  return (
    <div className="space-y-5" data-testid="recruiter-profile">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        {s.rank && (
          <span className="flex h-9 items-center gap-1.5 rounded-full accent-bg-100 px-3 font-display text-sm font-bold accent-text" data-testid="recruiter-rank">
            <Trophy className="h-4 w-4" /> #{s.rank} / {s.peers}
          </span>
        )}
        {s.code && <Badge variant="secondary" className="rounded-full font-mono">{s.code}</Badge>}
        {(s.association_names || []).map((a) => (
          <Badge key={a} variant="outline" className="gap-1 rounded-full"><Building2 className="h-3 w-3" />{a}</Badge>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">
          Actif du {s.first_date} au {s.last_date}
        </span>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="BS validés" value={fmtInt(s.bs_reel)} hint={`${fmtInt(s.bs_rue)} signés`} />
        <KpiVs label="Taux de transformation" value={fmtPct(s.taux_transfo)} delta={<VsAvg value={s.taux_transfo} avg={g.taux_transfo} pct />} />
        <KpiVs label="BS validés / jour actif" value={fmtNum(s.bs_par_jour, 2)} delta={<VsAvg value={s.bs_par_jour} avg={g.bs_par_jour_moyen} />} />
        <KpiVs label="Don moyen" value={fmtEur(s.don_moyen)} delta={<VsAvg value={s.don_moyen} avg={g.don_moyen} />} />
        <Kpi label="Montant mensuel récurrent" value={fmtEur(s.montant)} hint="généré (dons validés)" />
        <Kpi label="Jours actifs" value={fmtInt(s.jours_actifs)} />
        <Kpi label="Âge moyen donateurs" value={`${fmtNum(s.age_moyen, 1)} ans`} />
        <Kpi label="Portée" value={`${fmtInt(s.nb_spots)} spots`} hint={`${fmtInt(s.nb_campagnes)} campagnes`} />
      </div>

      {/* Best day / best spot / best association callouts */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {bestDay && (
          <Callout icon={CalendarDays} title="Jour le plus performant"
            primary={bestDay.jour}
            secondary={`${fmtNum(bestDay.bs_par_jour, 2)} BS validés / jour · ${bestDay.jours_actifs} jours · transfo ${fmtPct(bestDay.taux_transfo)}`} />
        )}
        {bestSpot && (
          <Callout icon={MapPin} title="Spot le plus performant"
            primary={bestSpot.lieu}
            secondary={`${fmtNum(bestSpot.bs_par_jour, 2)} BS validés / jour · ${bestSpot.jours_actifs} jours · transfo ${fmtPct(bestSpot.taux_transfo)}`} />
        )}
        {bestAssoc && (
          <Callout icon={Building2} title="Association la plus performante"
            primary={bestAssoc.name || bestAssoc.code}
            secondary={`${fmtNum(bestAssoc.bs_par_jour, 2)} BS validés / jour · ${fmtInt(bestAssoc.bs_reel)} BS validés · transfo ${fmtPct(bestAssoc.taux_transfo)}`} />
        )}
      </div>

      <Tabs defaultValue="jours" className="w-full">
        <TabsList className="flex-wrap">
          <TabsTrigger value="jours" data-testid="tab-jours">Jours & Lieux</TabsTrigger>
          <TabsTrigger value="evolution" data-testid="tab-evolution">Évolution</TabsTrigger>
          <TabsTrigger value="donateurs" data-testid="tab-donateurs">Profil donateurs</TabsTrigger>
          <TabsTrigger value="campagnes" data-testid="tab-campagnes">Campagnes & canaux</TabsTrigger>
        </TabsList>

        {/* Jours & Lieux */}
        <TabsContent value="jours" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SectionCard title="Performance par jour de la semaine" subtitle="BS validés · rendement par jour en sous-titre">
              <HorizontalBars data={weekdayData} subLabels={weekdaySub} yWidth={92}
                height={280} highlightTop={false} formatTip={(v) => `${fmtInt(v)} BS validés`} />
            </SectionCard>
            <SectionCard title="Meilleurs spots" subtitle="Top lieux par BS validés (rendement en sous-titre)">
              {spotData.length === 0
                ? <p className="py-8 text-center text-sm text-muted-foreground">Aucun spot sur la période.</p>
                : <HorizontalBars data={spotData} subLabels={spotSub} yWidth={168}
                    height={Math.max(240, spotData.length * 46)} highlightTop={false} formatTip={(v) => `${fmtInt(v)} BS validés`} />}
            </SectionCard>
          </div>
          {(data.spots || []).length > 0 && (
            <SectionCard title="Détail des spots" subtitle="Tous les lieux où ce recruteur a travaillé">
              <div className="max-h-72 overflow-y-auto rounded-xl border">
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent">
                    <TableHead>Lieu</TableHead><TableHead className="text-right">BS validés</TableHead>
                    <TableHead className="text-right">Jours</TableHead><TableHead className="text-right">BS/jour</TableHead>
                    <TableHead className="text-right">Transfo.</TableHead><TableHead className="text-right">Don moyen</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.spots.map((sp) => (
                      <TableRow key={sp.lieu} data-testid="spot-row">
                        <TableCell className="break-words text-sm font-medium">{sp.lieu}</TableCell>
                        <TableCell className="text-right tabular">{fmtInt(sp.bs_reel)}</TableCell>
                        <TableCell className="text-right tabular text-muted-foreground">{fmtInt(sp.jours_actifs)}</TableCell>
                        <TableCell className="text-right tabular">{fmtNum(sp.bs_par_jour, 2)}</TableCell>
                        <TableCell className="text-right tabular">{fmtPct(sp.taux_transfo)}</TableCell>
                        <TableCell className="text-right tabular text-muted-foreground">{fmtEur(sp.don_moyen)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          )}
        </TabsContent>

        {/* Évolution */}
        <TabsContent value="evolution" className="mt-4 space-y-4">
          <RecruiterEvolution recruiter={recruiter} body={body} />
        </TabsContent>

        {/* Profil donateurs */}
        <TabsContent value="donateurs" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SectionCard title="Répartition par âge" subtitle="Donateurs validés recrutés">
              {ageData.every((a) => !a.value)
                ? <p className="py-8 text-center text-sm text-muted-foreground">Âge non renseigné.</p>
                : <HorizontalBars data={ageData} yWidth={92} height={240} formatTip={(v) => `${fmtInt(v)} donateurs`} />}
            </SectionCard>
            <SectionCard title="Répartition par genre" subtitle="Donateurs validés recrutés">
              <div className="space-y-3 pt-2">
                <DistBar label="Femmes" value={genre.F || 0} total={genreTotal} color="var(--tenant-accent-500)" />
                <DistBar label="Hommes" value={genre.H || 0} total={genreTotal} color="var(--tenant-accent-300, #9aa7b8)" />
                {genre.Autre > 0 && <DistBar label="Autre" value={genre.Autre} total={genreTotal} color="#C7CDD6" />}
              </div>
            </SectionCard>
          </div>
        </TabsContent>

        {/* Campagnes & canaux */}
        <TabsContent value="campagnes" className="mt-4 space-y-4">
          {byAssoc.length > 0 && (
            <SectionCard title="Performance par association" subtitle="Où ce recruteur est le plus fort">
              <div className="overflow-hidden rounded-xl border">
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent">
                    <TableHead>Association</TableHead><TableHead className="text-right">BS validés</TableHead>
                    <TableHead className="text-right">BS/jour</TableHead><TableHead className="text-right">Jours</TableHead>
                    <TableHead className="text-right">Transfo.</TableHead><TableHead className="text-right">Don moyen</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {byAssoc.map((a) => {
                      const isBest = bestAssoc && a.code === bestAssoc.code;
                      return (
                        <TableRow key={a.code} className={isBest ? 'accent-bg-100/60' : ''} data-testid="assoc-row">
                          <TableCell className="text-sm font-medium">
                            <span className="inline-flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />{a.name || a.code}
                              {isBest && <BadgeCheck className="h-4 w-4 accent-text" />}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular">{fmtInt(a.bs_reel)}</TableCell>
                          <TableCell className="text-right tabular font-semibold">{fmtNum(a.bs_par_jour, 2)}</TableCell>
                          <TableCell className="text-right tabular text-muted-foreground">{fmtInt(a.jours_actifs)}</TableCell>
                          <TableCell className="text-right tabular">{fmtPct(a.taux_transfo)}</TableCell>
                          <TableCell className="text-right tabular text-muted-foreground">{fmtEur(a.don_moyen)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          )}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SectionCard title="Répartition par canal" subtitle="BS validés">
              {(data.by_canal || []).length === 0
                ? <p className="py-6 text-center text-sm text-muted-foreground">—</p>
                : <div className="space-y-3 pt-2">
                    {data.by_canal.map((c) => (
                      <DistBar key={c.key} label={c.key} value={c.bs_reel} total={s.bs_reel} />
                    ))}
                  </div>}
            </SectionCard>
            <SectionCard title="Répartition par région" subtitle="BS validés">
              {(data.by_region || []).length === 0
                ? <p className="py-6 text-center text-sm text-muted-foreground">—</p>
                : <div className="space-y-3 pt-2">
                    {data.by_region.map((c) => (
                      <DistBar key={c.key} label={c.key} value={c.bs_reel} total={s.bs_reel} />
                    ))}
                  </div>}
            </SectionCard>
          </div>
          {(data.by_campaign || []).length > 0 && (
            <SectionCard title="Détail par campagne" subtitle="Codes campagne travaillés par ce recruteur">
              <div className="max-h-72 overflow-y-auto rounded-xl border">
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent">
                    <TableHead>Code</TableHead><TableHead>Canal · Région</TableHead>
                    <TableHead className="text-right">BS validés</TableHead><TableHead className="text-right">Transfo.</TableHead>
                    <TableHead className="text-right">Don moyen</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.by_campaign.map((c) => (
                      <TableRow key={c.code} data-testid="campaign-row">
                        <TableCell className="font-mono text-sm font-semibold">{c.code}</TableCell>
                        <TableCell className="break-words text-xs text-muted-foreground">{[c.canal, c.region].filter(Boolean).join(' · ')}</TableCell>
                        <TableCell className="text-right tabular">{fmtInt(c.bs_reel)}</TableCell>
                        <TableCell className="text-right tabular">{fmtPct(c.taux_transfo)}</TableCell>
                        <TableCell className="text-right tabular text-muted-foreground">{fmtEur(c.don_moyen)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
